// Developer APIs registry — the source of truth for the playground.
//
// Each EndpointDef describes a single GET endpoint:
//   • UI metadata (group, name, description, displayUrl, parameters)
//   • A `run()` async function that calls Supabase JS and returns the standard
//     response envelope `{ data, meta }`.
//
// v1 is internal preview: `run()` hits Supabase directly with the anon key.
// The displayUrl uses an aspirational `https://api.tagboard.io/v1/...` form so
// future edge-function migration can swap implementations without UI churn.

import {supabase, DEFAULT_MINE_ID} from '../../app/supabaseClient'

export const API_BASE_URL = 'https://api.tagboard.io/v1'

export type ParamType = 'string' | 'uuid' | 'number'

export interface ParamDef {
  name: string
  in: 'path' | 'query'
  type: ParamType
  required: boolean
  description: string
  placeholder?: string
  default?: string
}

export interface ResponseEnvelope {
  data: unknown
  meta: {
    count: number
    generated_at: string
    source: 'supabase-direct'
  }
}

export interface EndpointDef {
  id: string
  group: 'Personnel' | 'Equipment' | 'Tags' | 'Events' | 'Chambers' | 'Locations'
  name: string
  description: string
  method: 'GET'
  /** Canonical display URL (e.g. /personnel/:id). Combined with API_BASE_URL for snippets. */
  path: string
  params: ParamDef[]
  /** One-liner explaining when an integrator would use this endpoint. */
  useCase: string
  run: (params: Record<string, string>) => Promise<ResponseEnvelope>
}

// Helper to build the standard envelope from a Supabase response.
function envelope(data: unknown): ResponseEnvelope {
  const count = Array.isArray(data) ? data.length : data == null ? 0 : 1
  return {
    data,
    meta: {
      count,
      generated_at: new Date().toISOString(),
      source: 'supabase-direct',
    },
  }
}

// Surfaces Supabase errors in a way the playground can render predictably.
class APIError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message)
  }
}

function clampLimit(raw: string | undefined, fallback = 100, max = 500): number {
  const n = Number(raw ?? fallback)
  if (!Number.isFinite(n) || n <= 0) return fallback
  return Math.min(Math.floor(n), max)
}

export const ENDPOINTS: EndpointDef[] = [
  // ── Personnel ─────────────────────────────────────────────────────────
  {
    id: 'list-personnel',
    group: 'Personnel',
    name: 'List Personnel',
    description: 'Roster of all personnel scoped to the default mine.',
    method: 'GET',
    path: '/personnel',
    useCase:
      'Sync the live workforce list into your HR / shift-planning system; refresh every few minutes.',
    params: [
      {
        name: 'status',
        in: 'query',
        type: 'string',
        required: false,
        description: 'Filter by tag status: tagged_in | tagged_out | missing',
        placeholder: 'tagged_in',
      },
      {
        name: 'limit',
        in: 'query',
        type: 'number',
        required: false,
        description: 'Maximum rows (1-500). Default 100.',
        default: '100',
        placeholder: '100',
      },
    ],
    async run(params) {
      let q = supabase
        .from('personnel')
        .select(
          'id, name, employee_id, designation, crew, status, shift_id, location, photo, x, y, z, level, last_updated',
        )
        .eq('mine_id', DEFAULT_MINE_ID)
        .order('name')
        .limit(clampLimit(params.limit))
      if (params.status) q = q.eq('status', params.status)
      const {data, error} = await q
      if (error) throw new APIError(500, error.message, error.code)
      return envelope(data ?? [])
    },
  },
  {
    id: 'get-personnel-by-id',
    group: 'Personnel',
    name: 'Get Personnel by ID',
    description: 'A single personnel record plus any assigned tags.',
    method: 'GET',
    path: '/personnel/:id',
    useCase:
      'Look up a specific worker by id (e.g. from a tag-event payload) and fetch their current tag assignments.',
    params: [
      {
        name: 'id',
        in: 'path',
        type: 'string',
        required: true,
        description: 'Personnel id (TEXT). Format like `l9k2x7p_0abc...`.',
        placeholder: 'paste an id from List Personnel',
      },
    ],
    async run(params) {
      if (!params.id) throw new APIError(400, 'Missing required path parameter: id')
      const {data: person, error: pe} = await supabase
        .from('personnel')
        .select(
          'id, name, employee_id, designation, crew, status, shift_id, location, photo, x, y, z, level, last_updated',
        )
        .eq('id', params.id)
        .single()
      if (pe || !person) {
        throw new APIError(404, pe?.message ?? 'Personnel not found', pe?.code)
      }
      const {data: tags} = await supabase
        .from('tags')
        .select('id, tag_id, battery_level, signal_strength, is_connected, last_seen')
        .eq('assigned_to_id', params.id)
        .eq('assigned_to_type', 'personnel')
      return envelope({...person, tags: tags ?? []})
    },
  },

  // ── Equipment ─────────────────────────────────────────────────────────
  {
    id: 'list-equipment',
    group: 'Equipment',
    name: 'List Equipment',
    description: 'All equipment registered to the default mine.',
    method: 'GET',
    path: '/equipment',
    useCase:
      'Pull the fleet roster into an asset-management system; cross-reference asset_id with OEM serial numbers.',
    params: [
      {
        name: 'status',
        in: 'query',
        type: 'string',
        required: false,
        description: 'Filter by status: active | down | maintenance | idle',
        placeholder: 'active',
      },
      {
        name: 'limit',
        in: 'query',
        type: 'number',
        required: false,
        description: 'Maximum rows (1-500). Default 100.',
        default: '100',
        placeholder: '100',
      },
    ],
    async run(params) {
      let q = supabase
        .from('equipment')
        .select(
          'id, name, model, asset_id, activity, status, x, y, z, level, last_updated',
        )
        .eq('mine_id', DEFAULT_MINE_ID)
        .order('asset_id')
        .limit(clampLimit(params.limit))
      if (params.status) q = q.eq('status', params.status)
      const {data, error} = await q
      if (error) throw new APIError(500, error.message, error.code)
      return envelope(data ?? [])
    },
  },

  // ── Tags ──────────────────────────────────────────────────────────────
  {
    id: 'list-tags',
    group: 'Tags',
    name: 'List Tags',
    description: 'NFC / BLE tag registry with assignment + battery status.',
    method: 'GET',
    path: '/tags',
    useCase:
      'Detect low-battery tags or unassigned hardware before a shift; pipe alerts into your maintenance workflow.',
    params: [
      {
        name: 'assigned_to_type',
        in: 'query',
        type: 'string',
        required: false,
        description: 'Filter by what the tag is paired to: personnel | equipment',
        placeholder: 'personnel',
      },
      {
        name: 'limit',
        in: 'query',
        type: 'number',
        required: false,
        description: 'Maximum rows (1-500). Default 100.',
        default: '100',
        placeholder: '100',
      },
    ],
    async run(params) {
      let q = supabase
        .from('tags')
        .select(
          'id, tag_id, type, assigned_to_id, assigned_to_type, battery_level, signal_strength, is_connected, last_seen, mac_address',
        )
        .order('tag_id')
        .limit(clampLimit(params.limit))
      if (params.assigned_to_type) q = q.eq('assigned_to_type', params.assigned_to_type)
      const {data, error} = await q
      if (error) throw new APIError(500, error.message, error.code)
      return envelope(data ?? [])
    },
  },

  // ── Events ────────────────────────────────────────────────────────────
  {
    id: 'list-tag-events',
    group: 'Events',
    name: 'List Tag Events',
    description: 'Recent tag-in / tag-out events, most recent first.',
    method: 'GET',
    path: '/tag-events',
    useCase:
      'Build a shift-audit log or attendance feed. Poll every minute and de-dupe on event id.',
    params: [
      {
        name: 'hours',
        in: 'query',
        type: 'number',
        required: false,
        description: 'How far back to look, in hours (default 4).',
        default: '4',
        placeholder: '4',
      },
      {
        name: 'limit',
        in: 'query',
        type: 'number',
        required: false,
        description: 'Maximum rows (1-500). Default 100.',
        default: '100',
        placeholder: '100',
      },
    ],
    async run(params) {
      const hours = Math.max(1, Math.min(168, Number(params.hours ?? '4') || 4))
      const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString()
      const {data, error} = await supabase
        .from('tag_events')
        .select('id, tag_id, personnel_id, equipment_id, type, shift_id, timestamp, synced')
        .gte('timestamp', since)
        .order('timestamp', {ascending: false})
        .limit(clampLimit(params.limit))
      if (error) throw new APIError(500, error.message, error.code)
      return envelope(data ?? [])
    },
  },

  // ── Chambers ──────────────────────────────────────────────────────────
  {
    id: 'list-rescue-chambers',
    group: 'Chambers',
    name: 'List Rescue Chambers',
    description: 'All rescue chambers in the default mine with current occupancy.',
    method: 'GET',
    path: '/rescue-chambers',
    useCase:
      'Surface shelter capacity on an emergency-response dashboard. Combine with the occupancy endpoint for real-time fill.',
    params: [],
    async run() {
      const {data, error} = await supabase
        .from('rescue_chambers')
        .select(
          'id, name, location, location_description, capacity, current_occupancy, status, maintenance_status, x, y, z, level, access_point, last_inspection_date',
        )
        .eq('mine_id', DEFAULT_MINE_ID)
        .order('name')
      if (error) throw new APIError(500, error.message, error.code)
      return envelope(data ?? [])
    },
  },
  {
    id: 'get-chamber-occupancy',
    group: 'Chambers',
    name: 'Get Chamber Occupancy',
    description: 'Personnel currently sheltered in a specific chamber, with entry timestamps.',
    method: 'GET',
    path: '/rescue-chambers/:id/occupancy',
    useCase:
      'During an evacuation drill, mirror the chamber roster to a safety officer’s mobile app for headcount verification.',
    params: [
      {
        name: 'id',
        in: 'path',
        type: 'string',
        required: true,
        description: 'Chamber id from List Rescue Chambers.',
        placeholder: 'paste a chamber id',
      },
    ],
    async run(params) {
      if (!params.id) throw new APIError(400, 'Missing required path parameter: id')
      const {data: cp, error: ce} = await supabase
        .from('chamber_personnel')
        .select('id, personnel_id, entered_at')
        .eq('chamber_id', params.id)
        .order('entered_at', {ascending: false})
      if (ce) throw new APIError(500, ce.message, ce.code)
      const rows = cp ?? []
      const ids = rows.map(r => r.personnel_id as string)
      let people: Array<{id: string; name: string; employee_id: string; designation: string | null}> = []
      if (ids.length) {
        const {data: p, error: pe} = await supabase
          .from('personnel')
          .select('id, name, employee_id, designation')
          .in('id', ids)
        if (pe) throw new APIError(500, pe.message, pe.code)
        people = (p ?? []) as typeof people
      }
      const byId = new Map(people.map(p => [p.id, p]))
      const occupants = rows.map(r => {
        const p = byId.get(r.personnel_id as string)
        return {
          chamber_personnel_id: r.id,
          personnel_id: r.personnel_id,
          name: p?.name ?? null,
          employee_id: p?.employee_id ?? null,
          designation: p?.designation ?? null,
          entered_at: r.entered_at,
        }
      })
      return envelope({chamber_id: params.id, occupants})
    },
  },

  // ── Locations ─────────────────────────────────────────────────────────
  {
    id: 'list-locations',
    group: 'Locations',
    name: 'List Locations',
    description: 'All mining locations (entries, tunnels, stopes, etc.) in the default mine.',
    method: 'GET',
    path: '/locations',
    useCase:
      'Hydrate the spatial backbone for your own map / GIS overlay. Each location has x/y/z and a type enum.',
    params: [
      {
        name: 'type',
        in: 'query',
        type: 'string',
        required: false,
        description: 'Filter by location type: entry | tunnel | station | safety | infrastructure | stope | surface',
        placeholder: 'tunnel',
      },
    ],
    async run(params) {
      let q = supabase
        .from('locations')
        .select(
          'id, name, zone, level, type, description, priority, material, heading_status, tonnes_remaining, x, y, z',
        )
        .eq('mine_id', DEFAULT_MINE_ID)
        .order('name')
      if (params.type) q = q.eq('type', params.type)
      const {data, error} = await q
      if (error) throw new APIError(500, error.message, error.code)
      return envelope(data ?? [])
    },
  },
]

export function findEndpoint(id: string): EndpointDef | undefined {
  return ENDPOINTS.find(e => e.id === id)
}

// Build the fully-interpolated displayed URL given current parameter values.
// Path params replace `:name` segments; query params append `?k=v&...`.
export function buildDisplayUrl(endpoint: EndpointDef, params: Record<string, string>): string {
  let path = endpoint.path
  const query: string[] = []
  for (const p of endpoint.params) {
    const v = (params[p.name] ?? '').trim()
    if (p.in === 'path') {
      path = path.replace(`:${p.name}`, v || `:${p.name}`)
    } else if (v) {
      query.push(`${encodeURIComponent(p.name)}=${encodeURIComponent(v)}`)
    }
  }
  return `${API_BASE_URL}${path}${query.length ? '?' + query.join('&') : ''}`
}

// Re-export so the screen can do `error instanceof APIError`.
export {APIError}
